import { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Copy, ClipboardList, ChevronUp, ChevronDown, Eye, EyeOff, Save, X } from "lucide-react";
import {
  listarFormularios, criarFormulario, atualizarFormulario, excluirFormulario, duplicarFormulario,
  opcoesDoCampo, TIPOS_CAMPO_LABEL, TIPOS_COM_OPCOES,
  FormularioBriefing, CampoBriefing, TipoCampoBriefing,
} from "../services/briefings";
import { listarTiposTarefa } from "../services/tiposTarefa";
import { listarTiposProjeto } from "../services/tiposProjeto";
import { useConfirm } from "../components/ConfirmProvider";
import { useAuth } from "../hooks/useAuth";

// Construtor de formulários de briefing (estilo Google Forms). A gestão monta as
// perguntas e vincula a um tipo de tarefa e/ou tipo de projeto.
export function BriefingFormularios() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const { can } = useAuth();
  if (!can(["gestor", "admin"])) return <Navigate to="/" replace />;

  const [editandoId, setEditandoId] = useState<string | null>(null);

  const { data: formularios = [], isLoading } = useQuery({
    queryKey: ["briefings", "todos"],
    queryFn: () => listarFormularios({ todos: true }),
  });

  const criarM = useMutation({
    mutationFn: () => criarFormulario({ nome: "Novo briefing", campos: [] as any }),
    onSuccess: (f) => { qc.invalidateQueries({ queryKey: ["briefings"] }); setEditandoId(f.id); },
  });
  const duplicarM = useMutation({
    mutationFn: duplicarFormulario,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["briefings"] }),
  });
  const excluirM = useMutation({
    mutationFn: excluirFormulario,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["briefings"] }),
    onError: (e: any) => confirm({ title: "Não foi possível excluir", message: e?.response?.data?.error?.message || "Erro", confirmText: "OK" }),
  });
  const toggleM = useMutation({
    mutationFn: ({ id, ativo }: { id: string; ativo: boolean }) => atualizarFormulario(id, { ativo }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["briefings"] }),
  });

  async function aoExcluir(f: FormularioBriefing) {
    const ok = await confirm({
      title: `Excluir o formulário "${f.nome}"?`,
      message: "Se já houver briefings preenchidos com ele, a exclusão é bloqueada — desative em vez disso.",
      danger: true, confirmText: "Excluir",
    });
    if (ok) excluirM.mutate(f.id);
  }

  const emEdicao = formularios.find((f) => f.id === editandoId) || null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
            <ClipboardList size={20} /> Formulários de briefing
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Monte os formulários de briefing (como no Google Forms) e vincule a um <strong>tipo de tarefa</strong> e/ou <strong>tipo de projeto</strong>. Quem for preencher vê o formulário certo automaticamente.
          </p>
        </div>
        <button onClick={() => criarM.mutate()} disabled={criarM.isPending} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50">
          <Plus size={16} /> Novo formulário
        </button>
      </div>

      {isLoading && <p className="text-slate-400 text-sm">Carregando…</p>}
      {!isLoading && formularios.length === 0 && (
        <div className="text-center py-12 text-slate-400">
          <ClipboardList size={32} className="mx-auto mb-2 opacity-40" />
          Nenhum formulário ainda. Crie o primeiro.
        </div>
      )}

      <div className="space-y-2">
        {formularios.map((f) => (
          <div key={f.id} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl">
            <div className="flex items-center gap-3 px-4 py-3 flex-wrap">
              <button onClick={() => setEditandoId(editandoId === f.id ? null : f.id)} className="flex-1 text-left min-w-0">
                <span className="font-semibold text-slate-800 dark:text-slate-100">{f.nome}</span>
                <span className="text-xs text-slate-400 ml-2">{f.campos.length} pergunta{f.campos.length !== 1 && "s"}</span>
                {!f.ativo && <span className="ml-2 text-[10px] uppercase font-bold text-slate-500 bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded">inativo</span>}
              </button>
              <button onClick={() => toggleM.mutate({ id: f.id, ativo: !f.ativo })} className="p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 rounded" title={f.ativo ? "Desativar" : "Ativar"}>
                {f.ativo ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
              <button onClick={() => duplicarM.mutate(f.id)} className="p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 rounded" title="Duplicar (criar variação)">
                <Copy size={15} />
              </button>
              <button onClick={() => aoExcluir(f)} className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded" title="Excluir">
                <Trash2 size={15} />
              </button>
              <button onClick={() => setEditandoId(editandoId === f.id ? null : f.id)} className="text-sm text-blue-600 hover:underline font-medium">
                {editandoId === f.id ? "Fechar" : "Editar"}
              </button>
            </div>
            {editandoId === f.id && emEdicao && <EditorFormulario formulario={emEdicao} onFechar={() => setEditandoId(null)} />}
          </div>
        ))}
      </div>
    </div>
  );
}

function EditorFormulario({ formulario, onFechar }: { formulario: FormularioBriefing; onFechar: () => void }) {
  const qc = useQueryClient();
  const [nome, setNome] = useState(formulario.nome);
  const [descricao, setDescricao] = useState(formulario.descricao || "");
  const [tipoTarefaId, setTipoTarefaId] = useState(formulario.tipoTarefaId || "");
  const [tipoProjetoSlug, setTipoProjetoSlug] = useState(formulario.tipoProjetoSlug || "");
  const [campos, setCampos] = useState<CampoBriefing[]>(
    formulario.campos.map((c) => ({ ...c, opcoes: opcoesDoCampo(c) })),
  );

  // Recarrega ao trocar de formulário aberto.
  useEffect(() => {
    setNome(formulario.nome);
    setDescricao(formulario.descricao || "");
    setTipoTarefaId(formulario.tipoTarefaId || "");
    setTipoProjetoSlug(formulario.tipoProjetoSlug || "");
    setCampos(formulario.campos.map((c) => ({ ...c, opcoes: opcoesDoCampo(c) })));
  }, [formulario.id]);

  const { data: tiposTarefa = [] } = useQuery({ queryKey: ["tipos-tarefa", "todos"], queryFn: () => listarTiposTarefa({ todos: true }) });
  const { data: tiposProjeto = [] } = useQuery({ queryKey: ["tipos-projeto", "ativos"], queryFn: () => listarTiposProjeto() });

  const salvarM = useMutation({
    mutationFn: () => atualizarFormulario(formulario.id, {
      nome, descricao, tipoTarefaId: tipoTarefaId || null, tipoProjetoSlug: tipoProjetoSlug || null,
      campos: campos.map((c, i) => ({ ...c, ordem: i })) as any,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["briefings"] }); onFechar(); },
  });

  function addCampo() {
    setCampos((cs) => [...cs, { tipo: "texto", rotulo: "", obrigatorio: false, opcoes: [], permiteOutro: false, ordem: cs.length }]);
  }
  function upd(i: number, patch: Partial<CampoBriefing>) {
    setCampos((cs) => cs.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  }
  function remover(i: number) { setCampos((cs) => cs.filter((_, idx) => idx !== i)); }
  function mover(i: number, delta: number) {
    setCampos((cs) => {
      const alvo = i + delta;
      if (alvo < 0 || alvo >= cs.length) return cs;
      const novo = [...cs];
      [novo[i], novo[alvo]] = [novo[alvo], novo[i]];
      return novo;
    });
  }

  return (
    <div className="border-t border-slate-100 dark:border-slate-800 p-4 space-y-4">
      {/* Cabeçalho do formulário */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-slate-500">Nome do formulário</label>
          <input value={nome} onChange={(e) => setNome(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-800 text-sm outline-none focus:border-blue-500" placeholder="Ex.: BRIEFING | Vídeos e animações" />
        </div>
        <div>
          <label className="text-xs text-slate-500">Descrição (opcional)</label>
          <input value={descricao} onChange={(e) => setDescricao(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-800 text-sm outline-none focus:border-blue-500" placeholder="Instruções que aparecem no topo" />
        </div>
        <div>
          <label className="text-xs text-slate-500">Aparece no tipo de TAREFA</label>
          <select value={tipoTarefaId} onChange={(e) => setTipoTarefaId(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-800 text-sm outline-none">
            <option value="">— nenhum</option>
            {tiposTarefa.map((t: any) => <option key={t.id} value={t.id}>{t.icone} {t.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-slate-500">Aparece no tipo de PROJETO</label>
          <select value={tipoProjetoSlug} onChange={(e) => setTipoProjetoSlug(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-800 text-sm outline-none">
            <option value="">— nenhum</option>
            {tiposProjeto.map((t: any) => <option key={t.slug} value={t.slug}>{t.icone} {t.label}</option>)}
          </select>
        </div>
      </div>

      {/* Perguntas */}
      <div className="space-y-2">
        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Perguntas</p>
        {campos.length === 0 && <p className="text-sm text-slate-400">Nenhuma pergunta ainda. Adicione a primeira.</p>}
        {campos.map((c, i) => {
          const usaOpcoes = TIPOS_COM_OPCOES.includes(c.tipo);
          const ops = (Array.isArray(c.opcoes) ? c.opcoes : []) as string[];
          return (
            <div key={i} className="bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 rounded-lg p-3 space-y-2">
              <div className="flex items-start gap-2 flex-wrap">
                <span className="text-xs text-slate-400 mt-2.5 w-5 text-center shrink-0">{i + 1}</span>
                <input
                  value={c.rotulo}
                  onChange={(e) => upd(i, { rotulo: e.target.value })}
                  placeholder="Escreva a pergunta"
                  className="flex-1 min-w-[180px] px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-900 text-sm outline-none focus:border-blue-500"
                />
                <select
                  value={c.tipo}
                  onChange={(e) => upd(i, { tipo: e.target.value as TipoCampoBriefing })}
                  className="px-2 py-2 rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-900 text-sm outline-none"
                >
                  {Object.entries(TIPOS_CAMPO_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <label className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300 mt-2.5 cursor-pointer select-none">
                  <input type="checkbox" checked={c.obrigatorio} onChange={(e) => upd(i, { obrigatorio: e.target.checked })} />
                  Obrigatória
                </label>
                <div className="flex items-center gap-0.5 mt-1.5">
                  <button type="button" onClick={() => mover(i, -1)} disabled={i === 0} className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30" title="Subir"><ChevronUp size={14} /></button>
                  <button type="button" onClick={() => mover(i, 1)} disabled={i === campos.length - 1} className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30" title="Descer"><ChevronDown size={14} /></button>
                  <button type="button" onClick={() => remover(i)} className="p-1 text-red-400 hover:text-red-600" title="Remover pergunta"><Trash2 size={14} /></button>
                </div>
              </div>

              {usaOpcoes && (
                <div className="pl-7 space-y-1">
                  {ops.map((op, oi) => (
                    <div key={oi} className="flex items-center gap-2">
                      <span className="text-slate-300">•</span>
                      <input
                        value={op}
                        onChange={(e) => upd(i, { opcoes: ops.map((o, k) => (k === oi ? e.target.value : o)) })}
                        placeholder={`Opção ${oi + 1}`}
                        className="flex-1 px-2 py-1 rounded border border-slate-300 dark:border-slate-600 dark:bg-slate-900 text-sm outline-none"
                      />
                      <button type="button" onClick={() => upd(i, { opcoes: ops.filter((_, k) => k !== oi) })} className="text-slate-400 hover:text-red-500" title="Remover opção"><X size={13} /></button>
                    </div>
                  ))}
                  <div className="flex items-center gap-3 flex-wrap">
                    <button type="button" onClick={() => upd(i, { opcoes: [...ops, ""] })} className="text-xs text-blue-600 hover:underline">+ adicionar opção</button>
                    <label className="flex items-center gap-1.5 text-xs text-slate-500 cursor-pointer select-none">
                      <input type="checkbox" checked={c.permiteOutro} onChange={(e) => upd(i, { permiteOutro: e.target.checked })} />
                      incluir "Outro"
                    </label>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        <button type="button" onClick={addCampo} className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:underline font-medium">
          <Plus size={15} /> Adicionar pergunta
        </button>
      </div>

      <div className="flex items-center gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
        <button onClick={() => salvarM.mutate()} disabled={salvarM.isPending || !nome.trim()} className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50">
          <Save size={15} /> {salvarM.isPending ? "Salvando…" : "Salvar formulário"}
        </button>
        <button onClick={onFechar} className="px-3 py-2 rounded-lg text-sm bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700">Cancelar</button>
      </div>
    </div>
  );
}
